import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "./supabase.js";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recoveryMode, setRecoveryMode] = useState(false);

  // Initial session + subscribe to changes
  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      if (!mounted) return;
      if (event === "PASSWORD_RECOVERY") setRecoveryMode(true);
      if (event === "SIGNED_OUT") setRecoveryMode(false);
      setSession(next);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Load (or reload) profile whenever the authed user changes.
  // If the DB trigger missed this user (pre-trigger signup or OAuth edge case),
  // insert a default 'parent' row so FK constraints on it_children etc. pass.
  useEffect(() => {
    const user = session?.user;
    if (!user?.id) {
      setProfile(null);
      return;
    }
    let cancelled = false;

    const run = async () => {
      const columns = "id, email, display_name, role, plan_tier, plan_expires_at";
      const { data, error } = await supabase
        .from("it_users")
        .select(columns)
        .eq("id", user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) console.error("it_users fetch failed:", error);
      if (data) { setProfile(data); return; }

      // Missing row — self-heal via insert (allowed by 0005 RLS policy).
      const meta = user.user_metadata ?? {};
      const displayName =
        meta.full_name || meta.name || meta.display_name ||
        (user.email ? user.email.split("@")[0] : null);

      const { data: inserted, error: insErr } = await supabase
        .from("it_users")
        .insert({ id: user.id, email: user.email, display_name: displayName, role: "parent" })
        .select(columns)
        .single();
      if (cancelled) return;
      if (insErr) {
        console.error("it_users self-insert failed:", insErr);
        setProfile(null);
      } else {
        setProfile(inserted);
      }
    };

    run();
    return () => { cancelled = true; };
  }, [session?.user?.id]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setRecoveryMode(false);
    window.location.hash = "";
  };

  const value = { session, profile, loading, recoveryMode, signOut };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
